import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/solid';
import Image from 'next/image';
import Link from 'next/link';
import { Fragment, useState, useRef } from 'react';
import { useLocalStorage } from 'react-use';

import { createClient } from '@/utils/supabase/component';
import { validatePassword } from '@/utils/passwordPolicy';
import { useAnalytics } from '@/contexts/Analytics';
import PasswordStrengthHints from '@/components/PasswordStrengthHints';
import LastUsedBadge from '@/components/LastUsedBadge';
import { useLastUsedLoginMethod } from '@/hooks/useLastUsedLoginMethod';
import Turnstile, {
  isTurnstileEnabled,
  TURNSTILE_ERROR_MESSAGE,
  type TurnstileHandle,
} from '@/components/Turnstile';

const EMAIL_HISTORY_LIMIT = 3;

const normalizeEmail = (value: string) => value.trim().toLowerCase();

const buildUniqueEmailHistory = (entries: unknown): string[] => {
  if (!Array.isArray(entries)) return [];
  return entries.reduce<string[]>((acc, entry) => {
    if (typeof entry !== 'string') return acc;
    const trimmed = entry.trim();
    if (!trimmed) return acc;
    const normalized = normalizeEmail(trimmed);
    if (!acc.some((e) => normalizeEmail(e) === normalized)) {
      acc.push(trimmed);
    }
    return acc;
  }, []);
};

interface AuthModalProps {
  showModal: boolean;
  onClose: () => void;
  afterClose?: () => void;
  redirectTo: string;
  botName?: string;
  botAvatar?: string;
}

export default function AuthModal({
  showModal,
  onClose,
  afterClose,
  redirectTo,
  botName,
  botAvatar,
}: AuthModalProps) {
  const supabase = createClient();
  const analytics = useAnalytics();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: 'error' | 'success';
    text: string;
  } | null>(null);
  const [isSignUp, setIsSignUp] = useState(false);

  const [storedEmailHistory, setStoredEmailHistory] = useLocalStorage<string[]>(
    'emailHistory',
    [],
  );
  const { lastUsedLoginMethod, setLastUsedLoginMethod } =
    useLastUsedLoginMethod();

  const uniqueEmailHistory = buildUniqueEmailHistory(storedEmailHistory);
  const normalizedEmailValue = normalizeEmail(email);
  const hasReachedEmailLimit = uniqueEmailHistory.length >= EMAIL_HISTORY_LIMIT;
  const emailAlreadyTracked = normalizedEmailValue
    ? uniqueEmailHistory.some(
        (entry) => normalizeEmail(entry) === normalizedEmailValue,
      )
    : false;
  const isBlockedByEmailLimit =
    isSignUp &&
    normalizedEmailValue.length > 0 &&
    hasReachedEmailLimit &&
    !emailAlreadyTracked;
  const passwordValidation = validatePassword(password, email);
  // Supabase Auth rejects sign-in/sign-up without a captcha token once Attack
  // Protection is on.
  const captchaRef = useRef<TurnstileHandle>(null);
  const submitButtonDisabled =
    loading ||
    (isSignUp && isBlockedByEmailLimit) ||
    (isSignUp && password.length > 0 && !passwordValidation.isValid);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const captchaToken = await captchaRef.current?.getToken();
      if (isTurnstileEnabled && !captchaToken) {
        setMessage({ type: 'error', text: TURNSTILE_ERROR_MESSAGE });
        return;
      }

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      });

      if (error) {
        setMessage({ type: 'error', text: error.message });
      } else {
        setLastUsedLoginMethod('email');
        window.location.href = redirectTo;
      }
    } catch {
      setMessage({ type: 'error', text: 'An unexpected error occurred' });
    } finally {
      // Turnstile tokens are single use — clear it whatever the outcome.
      captchaRef.current?.reset();
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (normalizedEmailValue && hasReachedEmailLimit && !emailAlreadyTracked) {
      setMessage({
        type: 'error',
        text: `You've reached the limit of ${EMAIL_HISTORY_LIMIT} accounts on this device.`,
      });
      return;
    }

    if (!passwordValidation.isValid) {
      setMessage({
        type: 'error',
        text:
          passwordValidation.firstError ??
          'Password does not meet the security requirements.',
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch('/api/validate-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const validation = await response.json();
      if (!validation.isValid) {
        setMessage({
          type: 'error',
          text: validation.reason || 'Invalid email address',
        });
        setLoading(false);
        return;
      }

      const captchaToken = await captchaRef.current?.getToken();
      if (isTurnstileEnabled && !captchaToken) {
        setMessage({ type: 'error', text: TURNSTILE_ERROR_MESSAGE });
        return;
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/confirm?next=${encodeURIComponent(redirectTo)}`,
          captchaToken,
        },
      });

      if (error) {
        setMessage({ type: 'error', text: error.message });
      } else if ((data.user?.identities?.length ?? 0) === 0) {
        // Existing CONFIRMED account (e.g. created via Google): Supabase
        // anti-enumeration returns a fake "success" with an identity-less
        // user and sends NO email, so "check your email" would strand the
        // user. See login.tsx for the full story; same handling here.
        analytics.track('Sign Up Existing Account', {
          method: 'email',
          surface: 'modal',
        });
        // Fresh captcha token for the courtesy email BEFORE flipping the
        // view — the flip re-renders the widget with the 'login' action and
        // would void the pending token request.
        captchaRef.current?.reset();
        const noticeToken = await captchaRef.current?.getToken();
        setIsSignUp(false);
        setMessage({
          type: 'error',
          text: 'An account with this email already exists. Sign in below — if you originally signed up with Google, use the Google button, or reset your password.',
        });
        // Best-effort: emails the mailbox owner which provider they signed
        // up with plus a one-click sign-in link. Fire-and-forget by design.
        fetch('/api/notify-existing-account', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, captchaToken: noticeToken }),
        }).catch(() => undefined);
      } else {
        // See login.tsx: browser-side twin of the server-fired
        // `Sign Up Success`, carrying the session/referrer/UTMs the webhook
        // event cannot.
        analytics.track('Sign Up Started', {
          method: 'email',
          surface: 'modal',
        });
        // Lets the first authenticated load fire `Sign Up Completed` even if
        // the confirmation email is opened hours later (useUser.tsx).
        try {
          localStorage.setItem('ep_pending_signup', String(Date.now()));
        } catch {
          // storage unavailable — first-session detection still covers it.
        }

        setStoredEmailHistory((prev) => {
          const existing = buildUniqueEmailHistory(prev);
          if (
            normalizedEmailValue &&
            !existing.some((e) => normalizeEmail(e) === normalizedEmailValue)
          ) {
            existing.push(email.trim());
          }
          return existing.slice(0, EMAIL_HISTORY_LIMIT);
        });
        setMessage({
          type: 'success',
          text: 'Check your email for the confirmation link',
        });
      }
    } catch {
      setMessage({ type: 'error', text: 'An unexpected error occurred' });
    } finally {
      // Turnstile tokens are single use — clear it whatever the outcome.
      captchaRef.current?.reset();
      setLoading(false);
    }
  };

  const handleOAuthSignIn = async (provider: 'google' | 'facebook') => {
    setLoading(true);
    // Remember the chosen provider before redirecting to the OAuth flow so the
    // "Last used" hint is ready the next time the user opens this modal.
    setLastUsedLoginMethod(provider);
    try {
      const callbackUrl = `${window.location.origin}/auth/callback?next=${encodeURIComponent(redirectTo)}`;

      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: callbackUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });

      if (error) {
        setMessage({ type: 'error', text: error.message });
      }
    } catch {
      setMessage({ type: 'error', text: 'An unexpected error occurred' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition.Root show={showModal} as={Fragment} afterLeave={afterClose}>
      <Dialog as='div' className='relative z-[999]' onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity' />
        </Transition.Child>

        <div className='fixed inset-0 z-50 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 scale-95'
              enterTo='opacity-100 scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 scale-100'
              leaveTo='opacity-0 scale-95'
            >
              <Dialog.Panel className='relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all dark:bg-slate-900'>
                {/* Close button */}
                <button
                  type='button'
                  className='absolute right-4 top-4 z-10 rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-800 dark:hover:text-gray-300'
                  onClick={onClose}
                >
                  <XIcon className='h-5 w-5' />
                </button>

                <div className='px-8 pb-8 pt-10'>
                  {/* Bot context header */}
                  {botName && (
                    <div className='mb-6 flex flex-col items-center'>
                      {botAvatar ? (
                        <Image
                          src={botAvatar}
                          width={56}
                          height={56}
                          alt={botName}
                          className='mb-3 h-14 w-14 rounded-full object-cover ring-2 ring-gray-100 dark:ring-slate-700'
                        />
                      ) : (
                        <div className='mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-primary-400 to-primary-600 text-xl font-semibold text-white ring-2 ring-gray-100 dark:ring-slate-700'>
                          {botName.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <h2 className='text-center text-xl font-semibold text-gray-900 dark:text-white'>
                        {isSignUp
                          ? 'Sign up to chat with'
                          : 'Sign in to chat with'}
                      </h2>
                      <p className='text-center text-lg font-medium text-primary-600 dark:text-primary-400'>
                        {botName}
                      </p>
                    </div>
                  )}

                  {!botName && (
                    <div className='mb-6 flex flex-col items-center'>
                      <Image
                        src='/images/marky.png'
                        width={140}
                        height={28}
                        alt='Easy-Peasy.AI'
                        className='mb-4'
                      />
                      <h2 className='text-center text-xl font-semibold text-gray-900 dark:text-white'>
                        {isSignUp ? 'Create your free account' : 'Welcome back'}
                      </h2>
                    </div>
                  )}

                  {/* OAuth buttons */}
                  <div className='space-y-3'>
                    {[
                      {
                        provider: 'google' as const,
                        label: 'Google',
                        icon: (
                          <svg
                            className='absolute left-4 h-5 w-5'
                            viewBox='0 0 24 24'
                          >
                            <path
                              fill='#4285F4'
                              d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                            />
                            <path
                              fill='#34A853'
                              d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                            />
                            <path
                              fill='#FBBC05'
                              d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                            />
                            <path
                              fill='#EA4335'
                              d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                            />
                          </svg>
                        ),
                      },
                      {
                        provider: 'facebook' as const,
                        label: 'Facebook',
                        icon: (
                          <svg
                            className='absolute left-4 h-5 w-5'
                            fill='#1877F2'
                            viewBox='0 0 24 24'
                          >
                            <path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' />
                          </svg>
                        ),
                      },
                    ].map(({ provider, label, icon }) => (
                      <button
                        key={provider}
                        type='button'
                        onClick={() => handleOAuthSignIn(provider)}
                        disabled={loading}
                        className='relative flex w-full items-center justify-center rounded-xl border border-gray-200 bg-white py-3 px-4 text-sm font-medium text-gray-700 shadow-sm transition-all hover:bg-gray-50 hover:shadow active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                      >
                        {icon}
                        <span>
                          {isSignUp
                            ? `Sign up with ${label}`
                            : `Sign in with ${label}`}
                        </span>
                        {lastUsedLoginMethod === provider && (
                          <LastUsedBadge label='Last used' />
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Divider */}
                  <div className='my-6'>
                    <div className='relative'>
                      <div className='absolute inset-0 flex items-center'>
                        <div className='w-full border-t border-gray-200 dark:border-gray-700' />
                      </div>
                      <div className='relative flex justify-center text-sm'>
                        <span className='bg-white px-3 text-gray-400 dark:bg-slate-900 dark:text-gray-500'>
                          or continue with email
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Email/password form */}
                  <form
                    onSubmit={isSignUp ? handleSignUp : handleSignIn}
                    className='space-y-4'
                  >
                    <div>
                      <input
                        id='auth-modal-email'
                        name='email'
                        type='email'
                        autoComplete='email'
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`block w-full rounded-xl border px-4 py-3 text-sm shadow-sm transition-colors placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500 ${
                          isBlockedByEmailLimit
                            ? 'border-red-300 focus:border-red-400 focus:ring-red-200 dark:border-red-500'
                            : 'border-gray-200 focus:border-primary-400 focus:ring-primary-100 dark:border-gray-600 dark:focus:ring-primary-900'
                        }`}
                        placeholder='Email address'
                      />
                      {isSignUp && isBlockedByEmailLimit && (
                        <p className='mt-1.5 text-xs text-red-500'>
                          Limit of {EMAIL_HISTORY_LIMIT} accounts per device
                          reached.
                        </p>
                      )}
                    </div>

                    <div>
                      <input
                        id='auth-modal-password'
                        name='password'
                        type='password'
                        autoComplete={
                          isSignUp ? 'new-password' : 'current-password'
                        }
                        required
                        minLength={isSignUp ? 12 : undefined}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        aria-invalid={
                          isSignUp && password.length > 0
                            ? !passwordValidation.isValid
                            : undefined
                        }
                        aria-describedby={
                          isSignUp
                            ? 'auth-modal-password-requirements'
                            : undefined
                        }
                        className='block w-full rounded-xl border border-gray-200 px-4 py-3 text-sm shadow-sm transition-colors placeholder:text-gray-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 focus:ring-offset-0 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500 dark:focus:ring-primary-900'
                        placeholder='Password'
                      />
                      {isSignUp && (
                        <div id='auth-modal-password-requirements'>
                          <PasswordStrengthHints
                            rules={passwordValidation.rules}
                          />
                        </div>
                      )}
                    </div>

                    {!isSignUp && (
                      <div className='text-right'>
                        <Link
                          href='/auth/forgot-password'
                          className='text-xs font-medium text-primary-600 hover:text-primary-500 dark:text-primary-400'
                        >
                          Forgot password?
                        </Link>
                      </div>
                    )}

                    <Turnstile
                      ref={captchaRef}
                      action={isSignUp ? 'signup' : 'login'}
                      className='flex justify-center'
                    />

                    <button
                      type='submit'
                      disabled={submitButtonDisabled}
                      className='relative flex w-full justify-center rounded-xl bg-primary-600 py-3 px-4 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-700 hover:shadow active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      {loading
                        ? isSignUp
                          ? 'Creating account...'
                          : 'Signing in...'
                        : isSignUp
                          ? 'Create account'
                          : 'Sign in'}
                      {!isSignUp && lastUsedLoginMethod === 'email' && (
                        <LastUsedBadge label='Last used' variant='onPrimary' />
                      )}
                    </button>

                    {message && (
                      <div
                        className={`rounded-xl p-3 text-sm ${
                          message.type === 'error'
                            ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                            : 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                        }`}
                      >
                        {message.text}
                      </div>
                    )}
                  </form>

                  {/* Toggle sign in / sign up */}
                  <div className='mt-6 text-center'>
                    <button
                      type='button'
                      onClick={() => {
                        setIsSignUp(!isSignUp);
                        setMessage(null);
                      }}
                      className='text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    >
                      {isSignUp
                        ? 'Already have an account? Sign in'
                        : "Don't have an account? Sign up"}
                    </button>
                  </div>

                  {/* Terms */}
                  <p className='mt-4 text-center text-xs text-gray-400 dark:text-gray-500'>
                    By continuing, you agree to the{' '}
                    <Link
                      href='/terms-of-use'
                      target='_blank'
                      className='text-gray-500 underline hover:text-gray-700 dark:text-gray-400'
                    >
                      Terms
                    </Link>{' '}
                    and{' '}
                    <Link
                      href='/privacy-policy'
                      target='_blank'
                      className='text-gray-500 underline hover:text-gray-700 dark:text-gray-400'
                    >
                      Privacy Policy
                    </Link>
                  </p>

                  {/* Trust badges */}
                  <div className='mt-6 flex items-center justify-center gap-4 border-t border-gray-100 pt-5 dark:border-gray-800'>
                    {[
                      { src: '/images/soc2.png', label: 'SOC2' },
                      { src: '/images/iso-27001.png', label: 'ISO 27001' },
                      { src: '/images/hipaa.png', label: 'HIPAA' },
                      { src: '/images/gdpr.png', label: 'GDPR' },
                    ].map((badge) => (
                      <div
                        key={badge.label}
                        className='flex flex-col items-center gap-0.5'
                      >
                        <Image
                          src={badge.src}
                          width={28}
                          height={28}
                          alt={badge.label}
                          className='h-7 w-7 opacity-60'
                        />
                        <span className='text-[10px] font-medium text-gray-400 dark:text-gray-500'>
                          {badge.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
